import type { WebsiteBlock } from '../../types'
import { useBranding } from '../../contexts/BrandingContext'
import HeroBlock from './HeroBlock'
import FeaturesBlock from './FeaturesBlock'
import AboutBlock from './AboutBlock'
import ServicesBlock from './ServicesBlock'
import PricingBlock from './PricingBlock'
import FAQBlock from './FAQBlock'
import ContactBlock from './ContactBlock'
import TestimonialsBlock from './TestimonialsBlock'
import CTABlock from './CTABlock'
import StatsBlock from './StatsBlock'
import TeamBlock from './TeamBlock'
import TimelineBlock from './TimelineBlock'
import GalleryBlock from './GalleryBlock'
import MenuBlock from './MenuBlock'
import ComplianceBlock from './ComplianceBlock'
import CustomBlock from './CustomBlock'

const blockComponents: Record<string, React.ComponentType<{ block: WebsiteBlock }>> = {
  hero: HeroBlock,
  features: FeaturesBlock,
  about: AboutBlock,
  services: ServicesBlock,
  pricing: PricingBlock,
  faq: FAQBlock,
  contact: ContactBlock,
  testimonials: TestimonialsBlock,
  cta: CTABlock,
  stats: StatsBlock,
  team: TeamBlock,
  timeline: TimelineBlock,
  gallery: GalleryBlock,
  menu: MenuBlock,
  compliance: ComplianceBlock,
  custom: CustomBlock,
}

interface BlockRendererProps {
  block: WebsiteBlock
  isEditing?: boolean
  isSelected?: boolean
  onSelect?: (blockId: string) => void
}

export default function BlockRenderer({ block, isEditing = false, isSelected = false, onSelect }: BlockRendererProps) {
  const { branding } = useBranding()
  const Component = blockComponents[block.blockType]

  if (!block.visible) return null
  if (!Component) return null

  // In public mode, render the component directly — no wrapper needed.
  // Each block handles its own empty-content guard (returns null if no content).
  if (!isEditing) {
    return <Component block={block} />
  }

  // Editing mode: wrap with selection ring, drag handle area, and type label
  return (
    <div
      className="relative group overflow-hidden cursor-pointer"
      onClick={() => onSelect?.(block.id)}
    >
      <div className={`absolute inset-0 z-10 pointer-events-none transition-all duration-150 ${
        isSelected
          ? 'ring-2 ring-blue-500 ring-offset-1'
          : 'group-hover:ring-2 group-hover:ring-blue-300 group-hover:ring-ring-offset-1'
      }`} />
      {isSelected && (
        <div className="absolute -top-6 left-2 z-20 px-2 py-0.5 bg-blue-500 text-white text-[10px] rounded font-medium">
          {block.blockType}
        </div>
      )}
      <Component block={block} />
    </div>
  )
}
